<template>
	<div v-if="autoConnecting" class="auto-connect-loader">
		<p>Свързване към чата...</p>
	</div>
	<NetworkForm v-else :handle-submit="handleSubmit" :defaults="defaults" :disabled="disabled" />
</template>
<script lang="ts">
import {defineComponent, ref, onMounted, onUnmounted} from "vue";
import socket from "../../js/socket";
import {useStore} from "../../js/store";
import NetworkForm, {NetworkFormDefaults} from "../NetworkForm.vue";
export default defineComponent({
	name: "Connect",
	components: {
		NetworkForm,
	},
	props: {
		queryParams: Object,
	},
	setup(props) {
		const store = useStore();
		const disabled = ref(false);
		const autoConnecting = ref(false);
		const handleSubmit = (data: Record<string, any>) => {
			disabled.value = true;

			// ЛАФЧЕ.ORG: пазим ник/join/NickServ парола в sessionStorage
			// (в самия chat.lafche.org origin, не родителския lafche.org -
			// iframe-ите не могат да четат storage-а един на друг заради
			// same-origin policy) за 15 минути, за да можем автоматично
			// да "препратим" същия connect при reconnect със загубена
			// сесия (виж App.vue socket.io "reconnect" listener-а)
			if (data.nick) {
				try {
					// ЛАФЧЕ.ORG: data.join вече е обработен от
					// parseOverrideParams (интервали след запетаите,
					// "#sex, #VIP, ...") - при reload минава ПАК през
					// същата логика, но проверката за # префикс гледа
					// само първия символ, който сега е интервал (не #),
					// затова погрешно добавя ВТОРИ # -> развалени канали.
					// Изчистваме интервалите преди запис, за да е
					// безопасно за повторна обработка
					const cleanJoin =
						typeof data.join === "string"
							? data.join.replace(/,\s+/g, ",")
							: data.join;

					sessionStorage.setItem(
						"lafche_reconnect_info",
						JSON.stringify({
							nick: data.nick,
							join: cleanJoin,
							saslPassword: data.saslPassword,
							savedAt: Date.now(),
						})
					);
				} catch (e) {
					// sessionStorage недостъпен (private mode и т.н.) - не е критично
				}
			}

			socket.emit("network:new", data);
		};
		const parseOverrideParams = (params?: Record<string, string>) => {
			if (!params) {
				return {};
			}
			const parsedParams: Record<string, any> = {};
			for (let key of Object.keys(params)) {
				let value = params[key];
				// Param can contain multiple values in an array if its supplied more than once
				if (Array.isArray(value)) {
					value = value[0];
				}
				// Support `channels` as a compatibility alias with other clients
				if (key === "channels") {
					key = "join";
				}
				if (
					!Object.prototype.hasOwnProperty.call(
						store.state.serverConfiguration?.defaults,
						key
					)
				) {
					continue;
				}
				// When the network is locked, URL overrides should not affect disabled fields
				if (
					store.state.serverConfiguration?.lockNetwork &&
					["name", "host", "port", "tls", "rejectUnauthorized"].includes(key)
				) {
					continue;
				}
				if (key === "join") {
					value = value
						.split(",")
						.map((chan) => {
							// ЛАФЧЕ.ORG: .trim() ПРЕДИ проверката за #/&/!/+
							// префикс - без него, водещ интервал (напр.
							// " #VIP" след split по ", ") правеше проверката
							// невярна и добавяше ВТОРИ # ("# #VIP"),
							// натрупващо се при всяко повторно обработване
							// на вече обработен join низ (напр. при нашия
							// reconnect-reload механизъм)
							chan = chan.trim();

							if (!chan.match(/^[#&!+]/)) {
								return `#${chan}`;
							}
							return chan;
						})
						.join(", ");
				}
				// Override server provided defaults with parameters passed in the URL if they match the data type
				switch (typeof store.state.serverConfiguration?.defaults[key]) {
					case "boolean":
						if (value === "0" || value === "false") {
							parsedParams[key] = false;
						} else {
							parsedParams[key] = !!value;
						}
						break;
					case "number":
						parsedParams[key] = Number(value);
						break;
					case "string":
						parsedParams[key] = String(value);
						break;
				}
			}
			return parsedParams;
		};
		const defaults = ref<Partial<NetworkFormDefaults>>(
			Object.assign(
				{},
				store.state.serverConfiguration?.defaults,
				parseOverrideParams(props.queryParams)
			)
		);

		// ЛАФЧЕ.ORG патч: ако имаме nick от URL-а (винаги го подаваме от
		// нашия /chat/), auto-submit-ваме формата вместо да чакаме ръчен
		// клик - скриваме NetworkForm визуално, докато трае връзката.
		onMounted(() => {
			// ЛАФЧЕ.ORG патч: прилагаме темата от URL-а веднага, преди connect-а,
			// за да съвпада чата със сайта от самото начало (не само при клик).
			const lafcheTheme = props.queryParams?.theme;

			if (lafcheTheme === "lafche-light" || lafcheTheme === "lafche-dark") {
				void store.dispatch("settings/update", {
					name: "theme",
					value: lafcheTheme,
					sync: true,
				});
			}

			if (defaults.value.nick) {
				autoConnecting.value = true;
				handleSubmit(defaults.value);
			}
		});

		// ЛАФЧЕ.ORG патч: когато този компонент се размонтира, значи
		// връзката е успешна и приложението е превключило към чата -
		// казваме на родителската страница (в iframe-а) да покаже чата.
		onUnmounted(() => {
			try {
				window.parent.postMessage("thelounge-connected", "*");
			} catch (e) {
				// извън iframe контекст - няма родител, безопасно се игнорира
			}

			// ЛАФЧЕ.ORG патч: NickServ identify чрез директен socket "input"
			// emit - TheLounge null-ва "commands" полето за нови мрежи по
			// сигурностни причини (server.ts: data.commands = null), затова
			// симулираме реален потребителски вход директно, точно както
			// прави ChatInput.vue при нормално писане в чата.
			if (defaults.value.saslPassword) {
				setTimeout(() => {
					const nets = store.state.networks;
					const net = nets[nets.length - 1];

					if (net && net.channels && net.channels[0]) {
						socket.emit("input", {
							target: net.channels[0].id,
							text: "/msg NickServ identify " + defaults.value.saslPassword,
						});
					}
				}, 2000);
			}
		});

		return {
			defaults,
			disabled,
			handleSubmit,
			autoConnecting,
		};
	},
});
</script>
